import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { ResidentsIndexResponse } from "./types/residents/responses/ResidentsIndexResponse";
import { ResidentsIndexRequest } from "./types/residents/requests/ResidentsIndexRequest";
import { ResidentResponse } from "./types/residents/responses/ResidentResponse";
import { CreateResidentRequest } from "./types/residents/requests/CreateResidentRequest";
import { CreateResidentDietRequest } from "./types/residents/requests/CreateResidentDietRequest";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { CreateResidentAdmissionRequest } from "./types/residents/requests/CreateResidentAdmissionRequest";
import { VaccinationsForm } from "./types/residents/models/AddResidentVaccinationsForm";
import { UpdateOtherInformationRequest } from "./types/residents/requests/UpdateOtherInformationRequest";
import { StatusFlagsIndexResponse } from "./types/statusFlags/responses/StatusFlagsIndexResponse";
import { AssignStatusFlagRequest } from "./types/statusFlags/requests/AssignStatusFlagRequest";
import { ResidentStatusHistoryModel } from "./types/residents/models/ResidentStatusHistoryModel";

export const residentsApi = createApi({
  reducerPath: "residentsApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["Residents"],
  endpoints: (builder) => ({
    getResidentsIndex: builder.query<
      ResidentsIndexResponse,
      ResidentsIndexRequest | undefined
    >({
      query: (params) => ({
        url: "/residents",
        method: "GET",
        params,
      }),
      keepUnusedDataFor: 60 * 10,
      providesTags(result, _error, arg) {
        const page = arg?.page;
        const tags: Array<{ type: "Residents"; id?: string }> = [
          { type: "Residents" },
          page ? { type: "Residents", id: `p-${page}` } : { type: "Residents" },
        ];
        if (result?.data) {
          tags.push(
            ...result.data.map(({ id }) => ({
              type: "Residents" as const,
              id: `r-${id}`,
            })),
          );
        }
        return tags;
      },
    }),
    getResident: builder.query<ResidentResponse, { resident: number }>({
      query: ({ resident }) => ({
        url: `/residents/${resident}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { resident }) => [
        { type: "Residents", id: `r-${resident}` },
      ],
    }),
    deleteResident: builder.mutation<undefined, { residentId: number }>({
      query: ({ residentId }) => ({
        url: `/residents/${residentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Residents"],
    }),
    createResident: builder.mutation<ResidentResponse, CreateResidentRequest>({
      query: (body) => ({
        url: `/residents`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Residents"],
    }),
    updateResident: builder.mutation<
      ResidentResponse,
      { id: number; body: CreateResidentRequest }
    >({
      query: ({ id, body }) => ({
        url: `/residents/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Residents", id: `r-${id}` },
      ],
    }),
    uploadResidentProfileImage: builder.mutation<
      undefined,
      { residentId: number; body: FormData }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/media/profile-image`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { residentId }) => [
        { type: "Residents", id: `r-${residentId}` },
      ],
    }),
    updateResidentAllergies: builder.mutation<
      undefined,
      { residentId: number; body: FormData }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/allergies`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    deleteResidentAllergiesMedia: builder.mutation<
      undefined,
      { residentId: number; mediaId: number }
    >({
      query: ({ residentId, mediaId }) => ({
        url: `/residents/${residentId}/allergies-media/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateResidentDietCategories: builder.mutation<
      SuccessResponse,
      { residentId: number; body: CreateResidentDietRequest }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/diet-categories`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateResidentHealthConditions: builder.mutation<
      SuccessResponse,
      { resident?: number; body: FormData }
    >({
      query: ({ resident, body }) => ({
        url: `/residents/${resident}/health-conditions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.resident}` },
      ],
    }),
    deleteHealthConditionMedia: builder.mutation<
      undefined,
      { healthConditionId: number; mediaId: number }
    >({
      query: ({ healthConditionId, mediaId }) => ({
        url: `/residents/health-condition/${healthConditionId}/media/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.healthConditionId}` },
      ],
    }),
    updateResidentVaccination: builder.mutation<
      undefined,
      { residentId: number; body: VaccinationsForm }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/vaccination`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateResidentOtherInformation: builder.mutation<
      SuccessResponse,
      {
        residentId: number;
        body: UpdateOtherInformationRequest;
      }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/other-information`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateResidentAdmissions: builder.mutation<
      SuccessResponse,
      { residentId: number; body: CreateResidentAdmissionRequest }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/admission-info`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateOtherNotesMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; body: FormData }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/media/other-notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    deleteOtherNotesMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; mediaId: number }
    >({
      query: ({ residentId, mediaId }) => ({
        url: `/residents/${residentId}/media/other-notes/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updatePortfolioMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; body: FormData }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/media/portfolio`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    deletePortfolioMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; mediaId: number }
    >({
      query: ({ residentId, mediaId }) => ({
        url: `/residents/${residentId}/media/portfolio/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateOtherAssessmentsMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; body: FormData }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/media/other-assessments`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    deleteOtherAssessmentsMedia: builder.mutation<
      SuccessResponse,
      { residentId: number; mediaId: number }
    >({
      query: ({ residentId, mediaId }) => ({
        url: `/residents/${residentId}/media/other-assessments/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Residents", id: `r-${arg.residentId}` },
      ],
    }),
    updateResidentStatusFlags: builder.mutation<
      StatusFlagsIndexResponse,
      { id: number; body: AssignStatusFlagRequest }
    >({
      query: ({ id, body }) => ({
        url: `/residents/${id}/status-flags`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Residents", id: `r-${id}` },
      ],
    }),
    getResidentStatusFlags: builder.query<
      StatusFlagsIndexResponse,
      { residentId: number }
    >({
      query: ({ residentId }) => ({
        url: `/residents/${residentId}/status-flags`,
        method: "GET",
      }),
      providesTags: (_result, _error, { residentId }) => [
        { type: "Residents", id: `r-${residentId}` },
      ],
    }),
    getResidentStatusHistory: builder.query<
      ResidentStatusHistoryModel[],
      { residentId: number }
    >({
      query: ({ residentId }) => ({
        url: `/residents/${residentId}/statuses`,
        method: "GET",
      }),
      providesTags: (_result, _error, { residentId }) => [
        { type: "Residents", id: `r-${residentId}` },
      ],
    }),
  }),
});

export const {
  useGetResidentsIndexQuery,
  useLazyGetResidentsIndexQuery,
  useDeleteResidentMutation,
  useGetResidentQuery,
  useCreateResidentMutation,
  useUpdateResidentMutation,
  useUploadResidentProfileImageMutation,
  useUpdateResidentAllergiesMutation,
  useDeleteResidentAllergiesMediaMutation,
  useUpdateResidentDietCategoriesMutation,
  useUpdateResidentHealthConditionsMutation,
  useDeleteHealthConditionMediaMutation,
  useUpdateResidentVaccinationMutation,
  useUpdateResidentOtherInformationMutation,
  useUpdateResidentAdmissionsMutation,
  useUpdateOtherNotesMediaMutation,
  useDeleteOtherNotesMediaMutation,
  useUpdatePortfolioMediaMutation,
  useDeletePortfolioMediaMutation,
  useUpdateOtherAssessmentsMediaMutation,
  useDeleteOtherAssessmentsMediaMutation,
  useUpdateResidentStatusFlagsMutation,
  useGetResidentStatusFlagsQuery,
  useGetResidentStatusHistoryQuery,
  usePrefetch,
} = residentsApi;
